import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import EyeSvg from '../../../../design-system/icons/core/eye.svg?component';
import EyeSlashSvg from '../../../../design-system/icons/core/eye-slash.svg?component';
import { RCIcon } from '../../RCIcon';
import { RCFormGroup } from '../RCFormGroup';
import type { FormattedString, TagType } from '../../types';

interface Props {
  /** ID of the input component, must be unique across the page */
  id: string;

  /** html attr, used by html form submission */
  name: string;

  /** Main label text (or more complex components if you really need to) */
  label: FormattedString;

  disabled?: boolean;

  /** Signals to browser's form validator that a non-empty value must be selected */
  required?: boolean;

  /** Validation error message */
  errorText?: string;
  /** Additional info displayed below label */
  helpText?: FormattedString;
  /** Annotation on the field, e.g. 'required', 'optional' */
  tag?: TagType;

  /** Hint to browser about the content of the field. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#values */
  autoComplete?: 'current-password' | 'new-password';

  /** called when value changes; updated value in evt.target.value */
  onChange: React.ChangeEventHandler<HTMLInputElement>;

  /** called when input element loses focus */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;

  /** Current value of the input */
  value: string;

  /** Text that appears when the value is empty */
  placeholder?: string;
}

/**
 * Password input with togglable show/hide button
 */
export function RCPasswordInput(props: Props) {
  const [showPassword, setShowPassword] = React.useState(false);

  return (
    <RCFormGroup
      errorText={props.errorText}
      helpText={props.helpText}
      inputId={props.id}
      label={props.label}
      tag={props.tag}
    >
      <div
        className={classNames(
          'rc-password-input',
          { 'rc-password-input--shown': showPassword },
        )}
      >
        <input
          autoComplete={props.autoComplete}
          disabled={props.disabled}
          id={props.id}
          name={props.name}
          onBlur={props.onBlur}
          onChange={props.onChange}
          placeholder={props.placeholder}
          required={props.required}
          type={showPassword ? 'text' : 'password'}
          value={props.value}
        />
        <button
          className="rc-password-input__toggle"
          onClick={() => setShowPassword(!showPassword)}
          type="button"
          title={showPassword ?
            I18n.t('commons.signupForm.hidePassword') :
            I18n.t('commons.signupForm.showPassword')
          }
        >
          {showPassword ?
            <RCIcon svgComponent={EyeSlashSvg} /> :
            <RCIcon svgComponent={EyeSvg} />
          }
        </button>
      </div>
    </RCFormGroup>
  );
}
